import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, ArrowLeft, Mail, Phone, Building2, MapPin, FileText, Calendar } from 'lucide-react';
import { clientsApi } from '@/api/clients';
import { cctvApi } from '@/api/cctv';
import { maintenanceApi } from '@/api/maintenance';
import type { CCTV, Maintenance } from '@/types';
import LoadingSpinner from '@/components/LoadingSpinner';
import ConfirmDialog from '@/components/ConfirmDialog';
import EmptyState from '@/components/EmptyState';
import DataTable from '@/components/DataTable';
import { formatDate, formatStatus, getStatusColor } from '@/utils/formatters';

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: clientRes, isLoading, error } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.getById(id!),
    enabled: !!id,
  });

  const { data: cctvRes } = useQuery({
    queryKey: ['cctv', 'byClient', id],
    queryFn: () => cctvApi.getAll({ clientId: id, limit: 50 }),
    enabled: !!id,
  });

  const { data: maintRes } = useQuery({
    queryKey: ['maintenance', 'byClient', id],
    queryFn: () => maintenanceApi.getAll({ clientId: id, limit: 50 }),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => clientsApi.remove(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Cliente eliminado exitosamente');
      navigate('/clients');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al eliminar el cliente');
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !clientRes?.data?.data) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-danger-200 bg-danger-50 p-8 text-center">
        <p className="text-sm font-medium text-danger-700">Cliente no encontrado</p>
        <button
          type="button"
          onClick={() => navigate('/clients')}
          className="mt-3 rounded-md bg-danger-100 px-3 py-1.5 text-sm font-medium text-danger-700 hover:bg-danger-200 transition-colors"
        >
          Volver a clientes
        </button>
      </div>
    );
  }

  const client = clientRes.data.data;
  const cctvList = (cctvRes?.data?.data || []) as CCTV[];
  const maintenanceList = (maintRes?.data?.data || []) as (Maintenance & { type?: string })[];

  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  const cctvColumns = [
    { key: 'brand', label: 'Marca', render: (item: CCTV) => item.brand || '-' },
    { key: 'model', label: 'Modelo', render: (item: CCTV) => item.model || '-' },
    { key: 'location', label: 'Ubicación', render: (item: CCTV) => item.location || '-' },
    {
      key: 'status',
      label: 'Estado',
      render: (item: CCTV) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(item.status)}`}>
          {formatStatus(item.status)}
        </span>
      ),
    },
  ];

  const maintColumns = [
    { key: 'scheduledDate', label: 'Fecha', render: (item: any) => formatDate(item.scheduledDate || item.createdAt) },
    { key: 'type', label: 'Tipo', render: (item: any) => (
      <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
        {item.type ? formatStatus(item.type) : '-'}
      </span>
    )},
    {
      key: 'status',
      label: 'Estado',
      render: (item: Maintenance) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(item.status)}`}>
          {formatStatus(item.status)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/clients')}
            className="rounded-md p-2 text-surface-400 hover:bg-surface-100 hover:text-surface-600 transition-colors"
            aria-label="Volver"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-surface-900">{client.name}</h1>
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(client.active ? 'active' : 'inactive')}`}>
            {client.active ? 'Activo' : 'Inactivo'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/clients/${id}/edit`)}
            className="inline-flex items-center gap-2 rounded-md border border-surface-300 bg-white px-3 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
          >
            <Edit className="h-4 w-4" />
            Editar
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteDialog(true)}
            className="inline-flex items-center gap-2 rounded-md border border-danger-300 bg-white px-3 py-2 text-sm font-medium text-danger-700 shadow-sm hover:bg-danger-50 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Eliminar
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-surface-900">Información General</h2>
          <dl className="space-y-3">
            <div className="flex items-start gap-3">
              <Building2 className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Empresa</dt>
                <dd className="text-sm text-surface-900">{client.company || 'No especificada'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Correo Electrónico</dt>
                <dd className="text-sm text-surface-900">{client.email || 'No especificado'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Teléfono</dt>
                <dd className="text-sm text-surface-900">{client.phone || 'No especificado'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Dirección</dt>
                <dd className="text-sm text-surface-900">{client.address || 'No especificada'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Fecha de Registro</dt>
                <dd className="text-sm text-surface-900">{formatDate(client.createdAt)}</dd>
              </div>
            </div>
            {client.notes && (
              <div className="flex items-start gap-3">
                <FileText className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Notas</dt>
                  <dd className="text-sm text-surface-900 whitespace-pre-wrap">{client.notes}</dd>
                </div>
              </div>
            )}
          </dl>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-surface-900">Equipos CCTV Asociados</h2>
            {cctvList.length > 0 ? (
              <DataTable<CCTV>
                columns={cctvColumns}
                data={cctvList}
                onRowClick={(item) => navigate(`/cctv/${item.id}`)}
              />
            ) : (
              <EmptyState title="Sin equipos CCTV" description="Este cliente no tiene equipos CCTV registrados" />
            )}
          </div>

          <div className="rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-surface-900">Historial de Mantenimientos</h2>
            {maintenanceList.length > 0 ? (
              <DataTable
                columns={maintColumns}
                data={maintenanceList}
                onRowClick={(item: any) => navigate(`/maintenance/${item.id}`)}
              />
            ) : (
              <EmptyState title="Sin mantenimientos" description="Este cliente no tiene mantenimientos registrados" />
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Eliminar Cliente"
        message={`¿Estás seguro de eliminar a ${client.name}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}

export default ClientDetail;
